import { useEffect, useMemo, useState } from "react";

import { Heart, HelpCircle, Images, Sparkles } from "lucide-react";

import type { KarmaUser } from "@/lib/karma/types";
import type { MatchOverview } from "@/lib/karma/match";
import { computeKarmaNumerology } from "@/lib/karma/numerology";
import { computeArchetype } from "@/lib/karma/archetypes";
import { getAge } from "@/lib/karma/age";
import { getDestinyTheme } from "@/lib/karma/theme";
import { cn } from "@/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

function getCardPhotos(user: KarmaUser) {
  const urls = user.photos.map((photo) => String(photo?.url ?? "").trim()).filter(Boolean);
  return (urls.length ? urls : ["/placeholder.svg"]).slice(0, 5);
}

function MetricPill({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-full bg-[#f8dbe8]/90 px-3 py-1 text-xs font-medium text-black/90 backdrop-blur">
      {label}: <span className="font-semibold">{Math.round(value / 10)}/10</span>
    </div>
  );
}

function getHighlightTags(user: KarmaUser, overview: MatchOverview, archetypeName: string) {
  const bio = String(user.bio ?? "")
    .split(/[,.;\n]/)
    .map((part) => part.trim())
    .filter((part) => part.length >= 4)
    .slice(0, 2);

  const tags = [
    `Arketip ${archetypeName}`,
    `${overview.categoryLabel} uyum`,
    `Ortak Yol ${overview.relationshipNumber}`,
    ...bio,
  ];

  return Array.from(new Set(tags)).slice(0, 6);
}

function buildNumerologyNarratives(args: {
  displayName: string;
  destinyNumber: number;
  expressionNumber: number;
  heartDesireNumber: number;
  archetypeName: string;
  relationshipNumber: number;
}) {
  const name = String(args.displayName ?? "").trim() || "Bu kişi";

  return [
    `${name} için yaşam yolu ${args.destinyNumber}; sahiplenme ve bağ kurma temalarını olgunlaştırarak ilişkilerde güvenli bir alan kurmaya yönelir.`,
    `${name} için ifade numarası ${args.expressionNumber}; iletişim dilini netleştirdikçe çevresiyle daha güçlü ve kalıcı bağ kurar.`,
    `${name} için kalp arzusu ${args.heartDesireNumber}; duygusal yakınlıkta samimiyet, sadakat ve huzur ihtiyacını öne çıkarır.`,
    `${name} ${args.archetypeName} arketipinde, ortak yol ${args.relationshipNumber} temasında karşılıklı gelişimi ve dengeyi besler.`,
  ];
}

function getJourneyBadges(user: KarmaUser) {
  if (Array.isArray(user.journeyBadges) && user.journeyBadges.length) {
    return user.journeyBadges.slice(0, 6);
  }

  return [
    { key: "aura:beyaz", label: "Beyaz Aura", short: "BA", type: "aura" as const },
    { key: "rank:tohum", label: "Tohum", short: "TO", type: "rank" as const },
  ];
}

function getInterestTags(user: KarmaUser) {
  const directInterests = Array.isArray(user.interests) ? user.interests.filter((tag) => typeof tag === "string" && tag.trim().length > 1) : [];
  return Array.from(new Set(directInterests)).slice(0, 10);
}

function InlinePhoto({ src, alt }: { src: string; alt: string }) {
  return (
    <div className="overflow-hidden rounded-2xl border border-white/60 bg-white/60 p-1.5 shadow-[inset_0_0_0_1px_rgba(124,58,237,0.08)]">
      <img src={src} alt={alt} className="h-44 w-full rounded-xl object-cover" loading="lazy" />
    </div>
  );
}

export function DiscoveryCard({
  user,
  overview,
  onLike,
  onOpenCompatibility,
  className,
  hideDetail,
}: {
  user: KarmaUser;
  overview: MatchOverview;
  onLike?: () => void;
  onOpenCompatibility?: () => void;
  className?: string;
  hideDetail?: boolean;
}) {
  const numerology = computeKarmaNumerology(user.displayName, user.birthDate);
  const archetype = computeArchetype(numerology);
  const theme = getDestinyTheme(numerology.destinyNumber);
  const photos = useMemo(() => getCardPhotos(user), [user]);
  const [activePhotoIndex, setActivePhotoIndex] = useState(0);
  const photo = photos[Math.min(activePhotoIndex, photos.length - 1)] ?? "/placeholder.svg";
  const age = getAge(user.birthDate);
  const hasMultiplePhotos = photos.length > 1;
  const highlights = useMemo(() => getHighlightTags(user, overview, archetype.name), [archetype.name, overview, user]);
  const narrativeLines = useMemo(
    () =>
      buildNumerologyNarratives({
        displayName: user.displayName,
        destinyNumber: numerology.destinyNumber,
        expressionNumber: numerology.expressionNumber,
        heartDesireNumber: numerology.heartDesireNumber,
        archetypeName: archetype.name,
        relationshipNumber: overview.relationshipNumber,
      }),
    [archetype.name, numerology.destinyNumber, numerology.expressionNumber, numerology.heartDesireNumber, overview.relationshipNumber, user.displayName],
  );
  const interestTags = useMemo(() => getInterestTags(user), [user]);
  const journeyBadges = useMemo(() => getJourneyBadges(user), [user]);

  useEffect(() => {
    setActivePhotoIndex(0);
  }, [user.id, photos.length]);

  return (
    <div
      className={cn(
        "relative overflow-hidden rounded-[2rem] border border-[#e9ddec] bg-[#f7edf7] shadow-sm",
        className,
      )}
    >
      <div className="relative aspect-[4/5] overflow-hidden rounded-[1.8rem]">
        <img src={photo} alt={user.displayName} className="absolute inset-0 h-full w-full object-cover" loading="lazy" />
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/35 via-black/10 to-transparent" />
        <div className={cn("pointer-events-none absolute inset-0 opacity-20", theme.gradient)} />

        <div className="absolute left-4 right-4 top-4 z-20 flex items-start justify-between gap-2">
          <div className="flex items-center gap-2">
            <div className="inline-flex items-center gap-2 rounded-full bg-black/35 px-3 py-1 text-xs font-semibold text-white backdrop-blur">
              <Sparkles className="h-3.5 w-3.5" /> Uyum {overview.scores.dailyHarmony}/100
            </div>

            {onOpenCompatibility ? (
              <button
                type="button"
                onClick={onOpenCompatibility}
                className="inline-flex items-center gap-1.5 rounded-full bg-black/35 px-3 py-1 text-xs font-semibold text-white backdrop-blur transition hover:bg-black/50"
              >
                <HelpCircle className="h-3.5 w-3.5" /> Neden?
              </button>
            ) : (
              <Dialog>
                <DialogTrigger asChild>
                  <button
                    type="button"
                    className="inline-flex items-center gap-1.5 rounded-full bg-black/35 px-3 py-1 text-xs font-semibold text-white backdrop-blur transition hover:bg-black/50"
                  >
                    <HelpCircle className="h-3.5 w-3.5" /> Neden?
                  </button>
                </DialogTrigger>
                <DialogContent className="max-w-xl">
                  <DialogHeader>
                    <DialogTitle>Nerede uyumlu, nerede zorlanır?</DialogTitle>
                    <DialogDescription>
                      İlişki, arkadaşlık veya iş birliği için Yaşam Yolu + Kalp/İfade + PIN özetidir.
                    </DialogDescription>
                  </DialogHeader>

                  <div className="grid gap-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">{overview.categoryLabel}</span>
                      <span className="rounded-full bg-muted px-3 py-1 text-xs font-medium text-muted-foreground">Ortak yol: {overview.relationshipNumber}</span>
                      <span className="rounded-full bg-muted px-3 py-1 text-xs font-medium text-muted-foreground">Kimya {Math.round(overview.scores.attraction / 10)}/10</span>
                      <span className="rounded-full bg-muted px-3 py-1 text-xs font-medium text-muted-foreground">Günlük {Math.round(overview.scores.dailyHarmony / 10)}/10</span>
                      <span className="rounded-full bg-muted px-3 py-1 text-xs font-medium text-muted-foreground">Uzun {Math.round(overview.scores.longevity / 10)}/10</span>
                    </div>

                    <div className="space-y-2 text-sm">
                      {overview.notes.map((n) => (
                        <div key={n} className="rounded-xl border bg-muted/30 px-4 py-3">
                          {n}
                        </div>
                      ))}
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            )}
          </div>

          <div className="inline-flex items-center gap-1.5 rounded-full bg-black/35 px-2.5 py-1 text-[11px] font-semibold text-white backdrop-blur">
            <Images className="h-3.5 w-3.5" /> {Math.min(photos.length, 5)}/5
          </div>
        </div>

        <div className="absolute bottom-3 left-4 right-4 z-20">
          <div className="inline-flex max-w-full items-center truncate rounded-full bg-[#f8dbe8]/95 px-4 py-1.5 font-display text-2xl font-semibold tracking-tight text-black shadow-sm">
            {user.displayName}, {age}
          </div>
        </div>
      </div>

      {photos.length > 1 ? (
        <div className="mt-3 flex flex-wrap items-center justify-center gap-2 px-4">
          {photos.map((photoUrl, index) => (
            <button
              key={`${user.id}-thumb-${index}`}
              type="button"
              onClick={() => setActivePhotoIndex(index)}
              className={cn(
                "h-10 w-10 overflow-hidden rounded-lg border-2 transition",
                activePhotoIndex === index ? "border-[var(--panel-primary)]" : "border-transparent",
              )}
              aria-label={`${index + 1}. fotoğraf`}
            >
              <img src={photoUrl} alt={`${user.displayName} ${index + 1}`} className="h-full w-full object-cover" loading="lazy" />
            </button>
          ))}
        </div>
      ) : null}

      <div className="space-y-4 px-4 pb-5 pt-4 text-[#1f152b]">
        <div className="flex flex-wrap items-center gap-2">
          <span className="rounded-full bg-white px-3 py-1 text-xs font-semibold">Kader {numerology.destinyNumber}</span>
          <span className="rounded-full bg-white px-3 py-1 text-xs font-semibold">Kalp {numerology.heartDesireNumber}</span>
          <span className="rounded-full bg-white px-3 py-1 text-xs font-semibold">İfade {numerology.expressionNumber}</span>
          <span className="rounded-full bg-white px-3 py-1 text-xs font-semibold">Arketip {archetype.name}</span>
        </div>

        <div className="rounded-2xl bg-white/80 p-3 shadow-[inset_0_0_0_1px_rgba(124,58,237,0.12)]">
          <div className="text-xs font-black uppercase tracking-[0.14em] text-[var(--panel-primary)]">Numerolojik Kimlik</div>
          <div className="mt-2 grid grid-cols-3 gap-2 text-center">
            <div className="rounded-xl bg-[#f5e8fb] px-2 py-2 text-[11px] font-bold text-[#4a2b60]">Yaşam Yolu<br />{numerology.destinyNumber}</div>
            <div className="rounded-xl bg-[#f5e8fb] px-2 py-2 text-[11px] font-bold text-[#4a2b60]">Kalp Arzusu<br />{numerology.heartDesireNumber}</div>
            <div className="rounded-xl bg-[#f5e8fb] px-2 py-2 text-[11px] font-bold text-[#4a2b60]">İfade No<br />{numerology.expressionNumber}</div>
          </div>
        </div>

        <div className="rounded-2xl bg-white/80 p-3 shadow-[inset_0_0_0_1px_rgba(124,58,237,0.12)]">
          <div className="text-xs font-black uppercase tracking-[0.14em] text-[var(--panel-primary)]">Kişisel Uyum</div>
          <div className="mt-2 flex flex-wrap gap-2">
            <MetricPill label="Kimya" value={overview.scores.attraction} />
            <MetricPill label="Günlük" value={overview.scores.dailyHarmony} />
            <MetricPill label="Uzun" value={overview.scores.longevity} />
          </div>
          <p className="mt-3 text-sm leading-relaxed text-black/80">{overview.notes[0]}</p>
        </div>

        {photos[1] ? <InlinePhoto src={photos[1]} alt={`${user.displayName} ek fotoğraf`} /> : null}

        <div className="rounded-2xl bg-white/70 p-3 shadow-[inset_0_0_0_1px_rgba(124,58,237,0.10)]">
          <div className="text-xs font-black uppercase tracking-[0.14em] text-[var(--panel-primary)]">Profil Notları</div>
          <div className="mt-2 text-sm leading-relaxed text-black/80">
            {user.bio?.trim() || "Bu profil henüz detaylı biyografi eklemedi."}
          </div>
          <div className="mt-3 space-y-2">
            {narrativeLines.map((line) => (
              <p key={line} className="rounded-xl bg-white/70 px-3 py-2 text-sm leading-relaxed text-black/80">
                {line}
              </p>
            ))}
          </div>
        </div>

        {photos[2] ? <InlinePhoto src={photos[2]} alt={`${user.displayName} portre`} /> : null}

        <div className="rounded-3xl border border-violet-200/70 bg-[#efe6f4] p-4 shadow-sm">
          <div className="mb-4 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <span className="text-[var(--panel-primary)]">✦</span>
              <h3 className="text-xl font-extrabold tracking-tight text-slate-900">İlgi Alanları</h3>
            </div>
            <span className="text-sm font-bold text-[var(--panel-primary)]">Tümünü Gör</span>
          </div>

          <div className="flex flex-wrap gap-3">
            {interestTags.map((tag) => (
              <span key={tag} className="inline-flex items-center gap-2 rounded-full border border-slate-100 bg-white px-5 py-2.5 text-sm font-medium text-slate-700 shadow-[0_1px_0_rgba(15,23,42,0.03)]">
                {tag}
                <span className="text-base leading-none">+</span>
              </span>
            ))}
          </div>
        </div>

        {photos[3] ? <InlinePhoto src={photos[3]} alt={`${user.displayName} galeri`} /> : null}

        <div className="rounded-3xl border border-violet-200/70 bg-[#efe6f4] p-4 text-center shadow-sm">
          <h3 className="text-3xl font-extrabold tracking-tight text-slate-900">Ruhsal Yolculuk Rozetleri</h3>
          <div className="mt-5 flex gap-5 overflow-x-auto pb-1">
            {journeyBadges.map((badge) => {
              const isWhiteAura = badge.key.includes("aura:beyaz") || badge.label.toLocaleLowerCase("tr-TR").includes("beyaz aura");
              return (
                <div key={badge.key} className="flex min-w-[96px] shrink-0 flex-col items-center gap-2">
                  <div
                    className={cn(
                      "grid h-16 w-16 place-items-center overflow-hidden rounded-full",
                      badge.imageUrl
                        ? "bg-transparent"
                        : isWhiteAura
                          ? "bg-gradient-to-br from-white via-slate-100 to-slate-200 ring-2 ring-violet-200 shadow-[0_0_22px_rgba(196,181,253,0.9)]"
                          : "bg-gradient-to-br from-orange-400 to-orange-600 text-white ring-2 ring-violet-200/80 shadow-[0_0_16px_rgba(124,58,237,0.25)]",
                    )}
                  >
                    {badge.imageUrl ? (
                      <img src={badge.imageUrl} alt={badge.label} className="h-full w-full object-cover" loading="lazy" />
                    ) : isWhiteAura ? (
                      <div className="h-[72%] w-[72%] rounded-full bg-white/80" />
                    ) : (
                      <span className="text-[12px] font-black">{badge.short || badge.label.slice(0, 2).toLocaleUpperCase("tr-TR")}</span>
                    )}
                  </div>
                  <span className="max-w-[96px] text-center text-sm font-bold leading-tight text-slate-700">{badge.label}</span>
                </div>
              );
            })}
          </div>

        </div>

        <div className="flex items-center justify-between gap-3">
          {onOpenCompatibility ? (
            <button
              type="button"
              onClick={onOpenCompatibility}
              className="rounded-full bg-[var(--panel-primary)] px-4 py-2 text-xs font-extrabold text-white transition hover:brightness-110"
            >
              Uyum Raporunu Gör
            </button>
          ) : <div />}
          {!hideDetail && onLike ? (
            <button
              type="button"
              onClick={onLike}
              className={cn(
                "grid h-12 w-12 place-items-center rounded-full text-white shadow-lg",
                "bg-gradient-to-br from-primary to-brand",
              )}
              aria-label="Beğen"
            >
              <Heart className="h-5 w-5" />
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
